import { useState, useEffect, useCallback, useRef, ChangeEvent } from "react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { Chip, Card, CardBody } from "@heroui/react";
import {
  Shield,
  User,
  FileText
} from "@phosphor-icons/react";
import { useSession, useUser } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import { InsurancePolicyStatus, InsurancePolicyComponent, InsurancePolicy } from "shared/types/insurance-policy";
import BuildingSelector from "./BuildingSelector";

interface AddPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (policy: any) => void;
  onUpdated?: (policy: any) => void;
  mode?: 'create' | 'edit' | 'view';
  policy?: InsurancePolicy | null;
}

const AddPolicyModal = ({ isOpen, onClose, onCreated, onUpdated, mode = 'create', policy = null }: AddPolicyModalProps) => {
  const { session } = useSession();
  const { user } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    policyNumber: "",
    status: InsurancePolicyStatus.ACTIVE,
    startDate: null as Date | null,
    endDate: null as Date | null,
    yearlyPremium: "",
    coverageAmount: "",
    exemptionAmount: "",
    notes: "",
    contactPersonName: "",
    contactPersonEmail: "",
    contactPersonPhone: "",
    insuranceCompanyId: null as number | null,
    brokerId: null as number | null,
    companyIds: [] as number[],
    buildingIds: [] as string[],
    components: [] as InsurancePolicyComponent[],
    policyDocumentFileLogIds: [] as number[],
    premiumPaymentFileLogIds: [] as number[]
  });

  // Available data
  const [availableComponentTypes, setAvailableComponentTypes] = useState<string[]>([]);
  const [selectedComponentTypes, setSelectedComponentTypes] = useState<string[]>([]);
  const [availableCompanies, setAvailableCompanies] = useState<Array<{ id: number; name: string }>>([]);
  // Backend-driven, debounced search results
  const [insurerQuery, setInsurerQuery] = useState("");
  const [insurerOptions, setInsurerOptions] = useState<any[]>([]);
  const [insurerLoading, setInsurerLoading] = useState(false);
  const [brokerQuery, setBrokerQuery] = useState("");
  const [brokerOptions, setBrokerOptions] = useState<any[]>([]);
  const [brokerLoading, setBrokerLoading] = useState(false);
  const [availableFileLogs, setAvailableFileLogs] = useState<any[]>([]);
  // Contactpersoon search
  const [contactQuery, setContactQuery] = useState("");
  const [contactOptions, setContactOptions] = useState<any[]>([]);
  const [contactLoading, setContactLoading] = useState(false);
  // Buildings selector
  const [buildingsOpen, setBuildingsOpen] = useState(false);
  // removed unused state to avoid re-renders
  const [policyFiles, setPolicyFiles] = useState<File[]>([]);
  const [premiumFiles, setPremiumFiles] = useState<Array<{ file: File; documentDate?: string }>>([]);

  // Component creation (legacy removed)

  const hasLoadedRef = useRef(false);
  const fetchInitialData = useCallback(async () => {
    if (!session) return;
    
    setIsLoadingData(true);
    try {
      const [componentTypesRes, companiesRes] = await Promise.all([
        fetchWithAuth(`${API_URL}/insurance/policies/component-types`, { method: "GET" }, session),
        fetchWithAuth(`${API_URL}/companies`, { method: "GET" }, session),
      ]);

      if (componentTypesRes.ok) {
        const types = await componentTypesRes.json();
        setAvailableComponentTypes(types);
      }
      if (companiesRes.ok) {
        const comps = await companiesRes.json();
        setAvailableCompanies((comps || []).map((c: any) => ({ id: c.id, name: c.name })));
      }
    } catch (error) {
      console.error("Failed to fetch initial data:", error);
      toast.error("Fout bij laden van gegevens");
    } finally {
      setIsLoadingData(false);
    }
  }, [session]);

  useEffect(() => {
    if (isOpen && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      fetchInitialData();
    }
    if (!isOpen) {
      hasLoadedRef.current = false;
    }
  }, [isOpen, fetchInitialData]);

  // Prefill when editing/viewing
  useEffect(() => {
    if (!isOpen) return;
    if (!policy) return;
    // Map incoming policy to form state
    setFormData(prev => ({
      ...prev,
      name: policy.name || "",
      description: policy.description || "",
      policyNumber: policy.policyNumber || "",
      status: policy.status || InsurancePolicyStatus.ACTIVE,
      startDate: policy.startDate ? new Date(policy.startDate as any) : null,
      endDate: policy.endDate ? new Date(policy.endDate as any) : null,
      yearlyPremium: policy.yearlyPremium != null ? String(policy.yearlyPremium) : "",
      coverageAmount: policy.coverageAmount != null ? String(policy.coverageAmount) : "",
      exemptionAmount: policy.exemptionAmount != null ? String(policy.exemptionAmount) : "",
      notes: policy.notes || "",
      contactPersonName: policy.contactPersonName || "",
      contactPersonEmail: policy.contactPersonEmail || "",
      contactPersonPhone: policy.contactPersonPhone || "",
      insuranceCompanyId: (policy as any).insuranceCompany?.id || null,
      brokerId: (policy as any).broker?.id || null,
      companyIds: (policy.companies || []).map((c: any) => c.id),
      buildingIds: (policy.buildings || []).map((b: any) => b.id),
      components: (policy.components || []) as any,
      policyDocumentFileLogIds: (policy as any).policyDocumentFileLogIds || [],
      premiumPaymentFileLogIds: (policy as any).premiumPaymentFileLogIds || []
    }));
    setSelectedComponentTypes((policy.components || []).map((c: any) => c.type));
    // prefill visible names for company inputs
    setInsurerQuery((policy as any).insuranceCompany?.name || "");
    setBrokerQuery((policy as any).broker?.name || "");
  }, [isOpen, policy]);

  // Lazy load file logs only when section is opened
  const fileLogsLoadedRef = useRef(false);
  const loadFileLogs = useCallback(async () => {
    if (!session || fileLogsLoadedRef.current) return;
    try {
      const fileLogsRes = await fetchWithAuth(`${API_URL}/file/get-files?fileLabel=polis`, { method: "GET" }, session);
      if (fileLogsRes.ok) {
        const fileLogs = await fileLogsRes.json();
        setAvailableFileLogs(fileLogs);
        // mark as loaded to avoid future fetches
        fileLogsLoadedRef.current = true;
      }
    } catch {}
  }, [session]);





  // Debounced backend search for insurer companies
  useEffect(() => {
    const q = insurerQuery.trim();
    if (!session) return;
    if (q.length < 2) { return; }
    const controller = new AbortController();
    const t = setTimeout(async () => {
      setInsurerLoading(true);
      try {
        const res = await fetchWithAuth(`${API_URL}/contact/search-companies?query=${encodeURIComponent(q)}`, { method: 'GET', signal: controller.signal as any }, session);
        if (res.ok) setInsurerOptions(await res.json());
      } catch {}
      setInsurerLoading(false);
    }, 350);
    return () => { controller.abort(); clearTimeout(t); };
  }, [insurerQuery, session]);

  // Debounced backend search for broker companies
  useEffect(() => {
    const q = brokerQuery.trim();
    if (!session) return;
    if (q.length < 2) { return; }
    const controller = new AbortController();
    const t = setTimeout(async () => {
      setBrokerLoading(true);
      try {
        const res = await fetchWithAuth(`${API_URL}/contact/search-companies?query=${encodeURIComponent(q)}`, { method: 'GET', signal: controller.signal as any }, session);
        if (res.ok) setBrokerOptions(await res.json());
      } catch {}
      setBrokerLoading(false);
    }, 350);
    return () => { controller.abort(); clearTimeout(t); };
  }, [brokerQuery, session]);

  // Debounced backend search for contacts (Contactpersoon)
  useEffect(() => {
    const q = contactQuery.trim();
    if (!session) return;
    if (q.length < 2) { return; }
    const controller = new AbortController();
    const t = setTimeout(async () => {
      setContactLoading(true);
      try {
        const res = await fetchWithAuth(`${API_URL}/contact/search?query=${encodeURIComponent(q)}`, { method: 'GET', signal: controller.signal as any }, session);
        if (res.ok) setContactOptions(await res.json());
      } catch {}
      setContactLoading(false);
    }, 350);
    return () => { controller.abort(); clearTimeout(t); };
  }, [contactQuery, session]);

  const handleClose = useCallback(() => {
    setFormData({
      name: "",
      description: "",
      policyNumber: "",
      status: InsurancePolicyStatus.ACTIVE,
      startDate: null,
      endDate: null,
      yearlyPremium: "",
      coverageAmount: "",
      exemptionAmount: "",
      notes: "",
      contactPersonName: "",
      contactPersonEmail: "",
      contactPersonPhone: "",
      insuranceCompanyId: null,
      brokerId: null,
      companyIds: [],
      buildingIds: [],
      components: [],
      policyDocumentFileLogIds: [],
      premiumPaymentFileLogIds: []
    });
    setSelectedComponentTypes([]);
    onClose();
  }, [onClose]);

  // no-op legacy

  const handleSubmit = useCallback(async () => {
    if (!session) return;

    if (!formData.name.trim() || !formData.policyNumber.trim()) {
      toast.error("Naam en polisnummer zijn verplicht");
      return;
    }

    setIsLoading(true);
    try {
      const submitData = {
        ...formData,
        name: formData.name.trim(),
        policyNumber: formData.policyNumber.trim(),
        yearlyPremium: formData.yearlyPremium ? parseFloat(formData.yearlyPremium) : null,
        coverageAmount: formData.coverageAmount ? parseFloat(formData.coverageAmount) : null,
        exemptionAmount: formData.exemptionAmount ? parseFloat(formData.exemptionAmount) : null,
        startDate: formData.startDate ? formData.startDate.toISOString().split('T')[0] : null,
        endDate: formData.endDate ? formData.endDate.toISOString().split('T')[0] : null,
        components: selectedComponentTypes.map(type => ({ type }))
      };

      const url = mode === 'edit' && policy?.id ? `${API_URL}/insurance/policies/${policy.id}` : `${API_URL}/insurance/policies`;
      const method = mode === 'edit' && policy?.id ? 'PUT' : 'POST';
      const response = await fetchWithAuth(
        url,
        {
          method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(submitData),
        },
        session
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const savedPolicy = await response.json();

      // Upload dropped files and auto-link
      const uploads: Promise<any>[] = [];
      const createdByUserId = user?.id;

      const uploadOne = async (file: File, fileType: 'document' | 'premium', extra?: { documentDate?: string }) => {
        const form = new FormData();
        form.append('file', file);
        const fileLogPayload: any = {
          fileLabel: fileType === 'premium' ? 'Verzekeringspolis Bewijs Premiebetaling' : 'Verzekeringspolis',
          createdByUserId,
          documentDate: extra?.documentDate
        };
        form.append('fileLog', JSON.stringify(fileLogPayload));
        form.append('customData', JSON.stringify({ insurancePolicyId: savedPolicy.id, fileType }));
        return fetchWithAuth(`${API_URL}/file/log`, { method: 'POST', body: form }, session);
      };

      for (const f of policyFiles) uploads.push(uploadOne(f, 'document'));
      for (const pf of premiumFiles) uploads.push(uploadOne(pf.file, 'premium', { documentDate: pf.documentDate }));
      if (uploads.length) await Promise.allSettled(uploads);

      const ref = await fetchWithAuth(`${API_URL}/insurance/policies/${savedPolicy.id}`, { method: 'GET' }, session);
      const finalPolicy = ref.ok ? await ref.json() : savedPolicy;

      toast.success(mode === 'edit' ? "Verzekeringspolis bijgewerkt!" : "Verzekeringspolis succesvol toegevoegd!");
      if (mode === 'edit') {
        onUpdated?.(finalPolicy);
      } else {
        onCreated?.(finalPolicy);
      }
      handleClose();
    } catch (error) {
      console.error("Failed to create policy:", error);
      toast.error(mode === 'edit' ? "Fout bij bijwerken van verzekeringspolis" : "Fout bij toevoegen van verzekeringspolis");
    } finally {
      setIsLoading(false);
    }
  }, [session, formData, selectedComponentTypes, user?.id, onCreated, onUpdated, handleClose, mode, policy?.id]);

  return (
    isOpen ? (
      <>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/40" onClick={handleClose} />
        <div className="relative w-full max-w-7xl h-[90vh] overflow-y-auto rounded-lg bg-white shadow-xl">
          <div className="sticky top-0 z-10 flex items-center gap-2 border-b bg-white px-6 py-4">
            <Shield className="h-5 w-5 text-moss-primary" />
            <div className="text-lg font-semibold text-neutral-primary">{mode === 'edit' ? 'Verzekeringspolis bewerken' : mode === 'view' ? 'Verzekeringspolis' : 'Nieuwe Verzekeringspolis'}</div>
            <div className="ml-auto flex items-center gap-2">
              <button onClick={handleClose} className="rounded-sm px-2 py-1 text-neutral-secondary-mid hover:text-neutral-primary">Sluiten</button>
            </div>
          </div>
          <div className="space-y-6 p-6">
          {isLoadingData ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-moss-primary"></div>
            </div>
          ) : (
            <>
              {/* Basic Information */}
              <Card>
                <CardBody className="space-y-4">
                  <h3 className="text-lg font-semibold text-neutral-primary">Basisinformatie</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-sm font-medium text-neutral-primary">Polisnaam <span className="text-red-600">*</span></label>
                      <input
                        type="text"
                        placeholder="Bijv. Bedrijfsaansprakelijkheid 2024"
                        className="w-full rounded-sm border border-neutral-primary/20 px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-moss-primary"
                        value={formData.name}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        required
                        disabled={mode === 'view'}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-sm font-medium text-neutral-primary">Polisnummer <span className="text-red-600">*</span></label>
                      <input
                        type="text"
                        placeholder="Bijv. POL-2024-001"
                        className="w-full rounded-sm border border-neutral-primary/20 px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-moss-primary"
                        value={formData.policyNumber}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, policyNumber: e.target.value }))}
                        required
                        disabled={mode === 'view'}
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-sm font-medium text-neutral-primary">Beschrijving</label>
                    <textarea
                      placeholder="Optionele beschrijving van de polis..."
                      className="min-h-[84px] w-full rounded-sm border border-neutral-primary/20 px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-moss-primary"
                      value={formData.description || ''}
                      onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      disabled={mode === 'view'}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-sm font-medium text-neutral-primary">Status <span className="text-red-600">*</span></label>
                      <select
                        className="w-full rounded-sm border border-neutral-primary/20 px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-moss-primary"
                        value={formData.status}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) => setFormData(prev => ({ ...prev, status: e.target.value as InsurancePolicyStatus }))}
                        disabled={mode === 'view'}
                      >
                        {Object.values(InsurancePolicyStatus).map((status) => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <label className="text-sm font-medium text-neutral-primary">Startdatum</label>
                        <input
                          type="date"
                          className="w-full rounded-sm border border-neutral-primary/20 px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-moss-primary"
                          value={formData.startDate ? new Date(formData.startDate).toISOString().split('T')[0] : ''}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, startDate: e.target.value ? new Date(e.target.value) : null }))}
                          disabled={mode === 'view'}
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-sm font-medium text-neutral-primary">Einddatum</label>
                        <input
                          type="date"
                          className="w-full rounded-sm border border-neutral-primary/20 px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-moss-primary"
                          value={formData.endDate ? new Date(formData.endDate).toISOString().split('T')[0] : ''}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, endDate: e.target.value ? new Date(e.target.value) : null }))}
                          disabled={mode === 'view'}
                        />
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>

              {/* Financial Information */}
              <Card>
                <CardBody className="space-y-4">
                  <h3 className="text-lg font-semibold text-neutral-primary">Financiële informatie</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <label className="text-sm font-medium text-neutral-primary">Jaarlijkse premie (€)</label>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        className="w-full rounded-sm border border-neutral-primary/20 px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-moss-primary"
                        value={formData.yearlyPremium as any}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, yearlyPremium: e.target.value }))}
                        disabled={mode === 'view'}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-sm font-medium text-neutral-primary">Dekkingsbedrag (€)</label>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        className="w-full rounded-sm border border-neutral-primary/20 px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-moss-primary"
                        value={formData.coverageAmount as any}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, coverageAmount: e.target.value }))}
                        disabled={mode === 'view'}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-sm font-medium text-neutral-primary">Eigen risico (€)</label>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        className="w-full rounded-sm border border-neutral-primary/20 px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-moss-primary"
                        value={formData.exemptionAmount as any}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, exemptionAmount: e.target.value }))}
                        disabled={mode === 'view'}
                      />
                    </div>
                  </div>
                </CardBody>
              </Card>

              {/* Policy Components */}
              <Card>
                <CardBody className="space-y-4">
                  <h3 className="text-lg font-semibold text-neutral-primary">Poliscomponenten</h3>
                  
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <div className="text-sm text-neutral-secondary-mid">Selecteer alle componenten die deze polis omvat.</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                        {availableComponentTypes.map((type) => {
                          const checked = selectedComponentTypes.includes(type);
                          return (
                            <label key={type} className="flex items-center gap-2 rounded-sm border border-neutral-primary/20 bg-white px-3 py-2 text-sm">
                              <input
                                type="checkbox"
                                className="h-4 w-4 accent-moss-primary"
                                checked={checked}
                                onChange={(e) => {
                                  setSelectedComponentTypes(prev => e.target.checked ? [...prev, type] : prev.filter(t => t !== type));
                                }}
                                disabled={mode === 'view'}
                              />
                              <span className="text-neutral-primary">{type}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>

              {/* Yally Companies to cover */}
              <Card>
                <CardBody className="space-y-4">
                  <h3 className="text-lg font-semibold text-neutral-primary">Gedekte Yally bedrijven</h3>
                  <div className="space-y-2">
                    <div className="text-sm text-neutral-secondary-mid">Selecteer alle bedrijven die door deze polis gedekt zijn.</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                      {availableCompanies.map((comp) => {
                        const checked = (formData.companyIds || []).includes(comp.id);
                        return (
                          <label key={comp.id} className="flex items-center gap-2 rounded-sm border border-neutral-primary/20 bg-white px-3 py-2 text-sm">
                            <input
                              type="checkbox"
                              className="h-4 w-4 accent-moss-primary"
                              checked={checked}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                companyIds: e.target.checked ? [...prev.companyIds, comp.id] : prev.companyIds.filter(id => id !== comp.id)
                              }))}
                              disabled={mode === 'view'}
                            />
                            <span className="text-neutral-primary">{comp.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </CardBody>
              </Card>

              {/* File Logs */}
              <Card>
                <CardBody className="space-y-4">
                  <h3 className="text-lg font-semibold text-neutral-primary">Documenten</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-neutral-primary">Polisdocumenten</label>
                      <div
                        className="border-2 border-dashed border-neutral-primary/20 rounded-md p-4 text-sm text-neutral-secondary-mid hover:border-moss-primary/50 transition-colors"
                        onDragOver={(e) => { e.preventDefault(); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          const files = Array.from(e.dataTransfer.files || []).filter(f => f);
                          if (files.length) setPolicyFiles(prev => [...prev, ...files]);
                        }}
                        onPaste={(e) => {
                          const files = Array.from(e.clipboardData.files || []).filter(f => f);
                          if (files.length) setPolicyFiles(prev => [...prev, ...files]);
                        }}
                      >
                        Sleep & plak polisdocument(en) hier of selecteer hieronder
                      </div>
                      {policyFiles.length > 0 && (
                        <div className="flex flex-col gap-2">
                          {policyFiles.map((f, idx) => (
                            <div key={idx} className="flex items-center justify-between rounded-md border border-neutral-primary/10 px-2 py-1 text-sm">
                              <span className="truncate max-w-[70%]">{f.name}</span>
                              <button className="text-red-500" onClick={() => setPolicyFiles(prev => prev.filter((_, i) => i !== idx))}>Verwijder</button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="space-y-1">
                        <select
                          multiple
                          className="min-h-[120px] w-full rounded-sm border border-neutral-primary/20 px-2 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-moss-primary"
                          onFocus={() => loadFileLogs()}
                          value={(formData.policyDocumentFileLogIds || []).map(String)}
                          onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                            const ids = Array.from(e.target.selectedOptions).map(o => parseInt(o.value));
                            setFormData(prev => ({ ...prev, policyDocumentFileLogIds: ids }));
                          }}
                        >
                          {availableFileLogs
                            .filter(log => log.label?.includes('polis') || log.label?.includes('verzekering'))
                            .map((log) => (
                              <option key={log.id} value={String(log.id)}>
                                {(log.summary || `Document ${log.id}`) + ' - ' + (log.buildingId || 'Geen gebouw')}
                              </option>
                          ))}
                        </select>
                      </div>
                      {formData.policyDocumentFileLogIds.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {formData.policyDocumentFileLogIds.map(id => {
                            return (
                              <Chip 
                                key={id} 
                                size="sm" 
                                variant="flat"
                                onClose={() => {
                                  setFormData(prev => ({
                                    ...prev,
                                    policyDocumentFileLogIds: prev.policyDocumentFileLogIds.filter(fid => fid !== id)
                                  }));
                                }}
                                className="bg-blue-100 text-blue-700"
                              >
                                Doc {id}
                              </Chip>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-neutral-primary">Premiebetalingen</label>
                      <div
                        className="border-2 border-dashed border-neutral-primary/20 rounded-md p-4 text-sm text-neutral-secondary-mid hover:border-moss-primary/50 transition-colors"
                        onDragOver={(e) => { e.preventDefault(); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          const files = Array.from(e.dataTransfer.files || []).filter(f => f);
                          if (files.length) setPremiumFiles(prev => [...prev, ...files.map(file => ({ file }))]);
                        }}
                        onPaste={(e) => {
                          const files = Array.from(e.clipboardData.files || []).filter(f => f);
                          if (files.length) setPremiumFiles(prev => [...prev, ...files.map(file => ({ file }))]);
                        }}
                      >
                        Sleep & plak betaalbewijzen hier of selecteer hieronder
                      </div>
                      {premiumFiles.length > 0 && (
                        <div className="flex flex-col gap-2">
                          {premiumFiles.map((pf, idx) => (
                            <div key={idx} className="flex items-center justify-between gap-2 rounded-md border border-neutral-primary/10 px-2 py-1 text-sm">
                              <span className="truncate max-w-[50%]">{pf.file.name}</span>
                              <input
                                type="date"
                                className="h-8 rounded-sm border border-neutral-primary/20 px-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-moss-primary"
                                value={pf.documentDate || ''}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setPremiumFiles(prev => prev.map((x, i) => i === idx ? { ...x, documentDate: e.target.value } : x))}
                              />
                              <button className="text-red-500" onClick={() => setPremiumFiles(prev => prev.filter((_, i) => i !== idx))}>Verwijder</button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="space-y-1">
                        <select
                          multiple
                          className="min-h-[120px] w-full rounded-sm border border-neutral-primary/20 px-2 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-moss-primary"
                          onFocus={() => loadFileLogs()}
                          value={(formData.premiumPaymentFileLogIds || []).map(String)}
                          onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                            const ids = Array.from(e.target.selectedOptions).map(o => parseInt(o.value));
                            setFormData(prev => ({ ...prev, premiumPaymentFileLogIds: ids }));
                          }}
                        >
                          {availableFileLogs
                            .filter(log => log.label?.includes('premie') || log.label?.includes('factuur') || log.label?.includes('betaling'))
                            .map((log) => (
                              <option key={log.id} value={String(log.id)}>
                                {(log.summary || `Document ${log.id}`) + ' - ' + (log.buildingId || 'Geen gebouw')}
                              </option>
                          ))}
                        </select>
                      </div>
                      {formData.premiumPaymentFileLogIds.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {formData.premiumPaymentFileLogIds.map(id => {
                            return (
                              <Chip 
                                key={id} 
                                size="sm" 
                                variant="flat"
                                onClose={() => {
                                  setFormData(prev => ({
                                    ...prev,
                                    premiumPaymentFileLogIds: prev.premiumPaymentFileLogIds.filter(fid => fid !== id)
                                  }));
                                }}
                                className="bg-green-100 text-green-700"
                              >
                                Prem {id}
                              </Chip>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>

              {/* Gebouwen selectie - Using DirectSearchComponent approach */}
              <Card className="relative">
                <CardBody className="space-y-6">
                  <div className="flex items-center gap-3">
                    <svg className="h-6 w-6 text-moss-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <h3 className="text-xl font-semibold text-neutral-primary">Gedekte gebouwen</h3>
                    {formData.buildingIds.length > 0 && (
                      <div className="rounded-full bg-moss-primary px-3 py-1 text-sm font-medium text-white">
                        {formData.buildingIds.length} geselecteerd
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-4">
                    <div className="text-neutral-secondary-mid">
                      Zoek en selecteer gebouwen. We tonen de referentiecodes (bijv. 2000-EMIE-12) met bedrijfsinformatie.
                    </div>
                    
                    {/* Building Selector Trigger */}
                    <button
                      type="button"
                      onClick={() => { 
                        if (mode !== 'view') { 
                          setBuildingsOpen(true);
                        } 
                      }}
                      className="flex items-center justify-between w-full rounded-2xl border-2 border-neutral-primary/20 bg-white px-8 py-6 text-left hover:border-moss-primary/50 transition-all duration-200 focus:outline-hidden focus:ring-4 focus:ring-moss-primary/10 focus:border-moss-primary shadow-lg hover:shadow-xl"
                      disabled={mode === 'view'}
                    >
                      <div className="flex items-center gap-6">
                        <div className="rounded-xl bg-moss-primary/10 p-4">
                          <svg className="h-8 w-8 text-moss-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-neutral-primary text-xl">
                            {formData.buildingIds.length > 0 
                              ? `${formData.buildingIds.length} gebouw${formData.buildingIds.length !== 1 ? 'en' : ''} geselecteerd`
                              : 'Selecteer gebouwen'
                            }
                          </div>
                          <div className="text-neutral-secondary-mid mt-2 text-base">
                            {formData.buildingIds.length > 0 
                              ? 'Klik om selectie aan te passen of meer gebouwen toe te voegen'
                              : 'Klik hier om alle gebouwen te selecteren die door deze polis gedekt zijn'
                            }
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {formData.buildingIds.length > 0 && (
                          <div className="rounded-lg bg-moss-primary/20 px-3 py-2 text-moss-primary font-semibold">
                            {formData.buildingIds.length}
                          </div>
                        )}
                        <svg 
                          className="h-6 w-6 text-neutral-secondary-mid transition-transform duration-200" 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </button>

                    {/* Selected buildings display */}
                    {(formData.buildingIds || []).length > 0 && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-semibold text-neutral-primary">Geselecteerde gebouwen:</div>
                          <div className="rounded-full bg-moss-primary/20 px-2 py-0.5 text-xs font-medium text-moss-primary">
                            {formData.buildingIds.length}
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {formData.buildingIds.map(id => (
                            <div key={id} className="group relative rounded-lg border border-moss-primary/20 bg-moss-primary/5 p-3 hover:bg-moss-primary/10 transition-colors">
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <svg className="h-4 w-4 text-moss-primary shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                    </svg>
                                    <span className="font-semibold text-moss-primary text-sm">{id}</span>
                                  </div>
                                </div>
                                {mode !== 'view' && (
                                  <button
                                    type="button"
                                    className="opacity-0 group-hover:opacity-100 ml-2 rounded-full p-1 text-moss-primary/60 hover:bg-moss-primary/20 hover:text-moss-primary transition-all shrink-0"
                                    onClick={() => setFormData(prev => ({ 
                                      ...prev, 
                                      buildingIds: prev.buildingIds.filter(bid => bid !== id) 
                                    }))}
                                    title="Verwijder gebouw"
                                  >
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        {mode !== 'view' && (
                          <div className="flex justify-end">
                            <button
                              type="button"
                              className="text-sm text-neutral-secondary-mid hover:text-neutral-primary transition-colors"
                              onClick={() => setFormData(prev => ({ ...prev, buildingIds: [] }))}
                            >
                              Alle gebouwen deselecteren
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>

              {/* Contact Information */}
              <Card>
                <CardBody className="space-y-4">
                  <h3 className="text-lg font-semibold text-neutral-primary">Contact informatie</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-sm font-medium text-neutral-primary">Verzekeringsmaatschappij <span className="text-red-600">*</span></label>
                      <input
                        type="text"
                        className="w-full rounded-sm border border-neutral-primary/20 px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-moss-primary"
                        placeholder="Zoek verzekeraar..."
                        value={insurerQuery}
                        onChange={(e) => setInsurerQuery(e.target.value)}
                        disabled={mode === 'view'}
                      />
                      {insurerLoading && (
                        <div className="text-xs text-neutral-secondary-mid">Zoeken…</div>
                      )}
                      {insurerQuery.trim().length >= 2 && insurerOptions.length > 0 && (
                        <div className="mt-1 max-h-40 w-full overflow-auto rounded-sm border border-neutral-primary/20 bg-white shadow-sm z-60 relative">
                          {insurerOptions.map((company) => (
                            <button
                              key={company.id}
                              type="button"
                              className="block w-full px-3 py-2 text-left text-sm hover:bg-primary/10"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, insuranceCompanyId: company.id }));
                                setInsurerQuery(company.name || "");
                                setInsurerOptions([]);
                              }}
                            >
                              {company.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="space-y-1">
                      <label className="text-sm font-medium text-neutral-primary">Makelaar</label>
                      <input
                        type="text"
                        className="w-full rounded-sm border border-neutral-primary/20 px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-moss-primary"
                        placeholder="Zoek makelaar..."
                        value={brokerQuery}
                        onChange={(e) => setBrokerQuery(e.target.value)}
                        disabled={mode === 'view'}
                      />
                      {brokerLoading && (
                        <div className="text-xs text-neutral-secondary-mid">Zoeken…</div>
                      )}
                      {brokerQuery.trim().length >= 2 && brokerOptions.length > 0 && (
                        <div className="mt-1 max-h-40 w-full overflow-auto rounded-sm border border-neutral-primary/20 bg-white shadow-sm z-60 relative">
                          {brokerOptions.map((company) => (
                            <button
                              key={company.id}
                              type="button"
                              className="block w-full px-3 py-2 text-left text-sm hover:bg-primary/10"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, brokerId: company.id }));
                                setBrokerQuery(company.name || "");
                                setBrokerOptions([]);
                              }}
                            >
                              {company.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <label className="text-sm font-medium text-neutral-primary">Contactpersoon</label>
                      <div className="flex items-center gap-2 rounded-sm border border-neutral-primary/20 px-3 py-2">
                        <User className="h-4 w-4 text-neutral-secondary-mid" />
                        <input
                          type="text"
                          className="w-full text-sm focus:outline-hidden"
                          placeholder="Zoek contact…"
                          value={contactQuery}
                          onChange={(e) => setContactQuery(e.target.value)}
                          disabled={mode === 'view'}
                        />
                      </div>
                      {contactLoading && (
                        <div className="text-xs text-neutral-secondary-mid">Zoeken…</div>
                      )}
                      {contactQuery.trim().length >= 2 && contactOptions.length > 0 && (
                        <div className="mt-1 max-h-40 w-full overflow-auto rounded-sm border border-neutral-primary/20 bg-white shadow-sm">
                          {contactOptions.map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              className="block w-full px-3 py-2 text-left text-sm hover:bg-primary/10"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, contactPersonName: `${c.firstName || ''} ${c.lastName || ''}`.trim(), contactPersonEmail: c.email || prev.contactPersonEmail, contactPersonPhone: c.phone_mobile || prev.contactPersonPhone }));
                                setContactQuery(`${c.firstName || ''} ${c.lastName || ''}`.trim());
                                setContactOptions([]); // close dropdown
                              }}
                            >
                              {`${c.firstName || ''} ${c.lastName || ''}`.trim()} {c.email ? `• ${c.email}` : ''}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="space-y-1">
                      <label className="text-sm font-medium text-neutral-primary">Email</label>
                      <input
                        type="email"
                        className="w-full rounded-sm border border-neutral-primary/20 px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-moss-primary"
                        value={formData.contactPersonEmail}
                        placeholder="Ontbreekt — (indien ontbreekt, later invullen in de contacten-database)"
                        onChange={(e) => setFormData(prev => ({ ...prev, contactPersonEmail: e.target.value }))}
                        disabled={mode === 'view'}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-sm font-medium text-neutral-primary">Telefoon</label>
                      <input
                        type="text"
                        className="w-full rounded-sm border border-neutral-primary/20 px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-moss-primary"
                        value={formData.contactPersonPhone}
                        placeholder="Ontbreekt — (indien ontbreekt, later invullen in de contacten-database)"
                        onChange={(e) => setFormData(prev => ({ ...prev, contactPersonPhone: e.target.value }))}
                        disabled={mode === 'view'}
                      />
                    </div>
                  </div>
                </CardBody>
              </Card>

              {/* Notes */}
              <Card>
                <CardBody>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-neutral-primary">Notities</label>
                    <div className="relative">
                      <div className="pointer-events-none absolute left-2 top-2">
                        <FileText className="h-4 w-4 text-neutral-secondary-mid" />
                      </div>
                      <textarea
                        className="w-full rounded-sm border border-neutral-primary/20 px-7 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-moss-primary"
                        placeholder="Extra opmerkingen over deze polis..."
                        value={formData.notes || ''}
                        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                        disabled={mode === 'view'}
                      />
                    </div>
                  </div>
                </CardBody>
              </Card>
            </>
          )}
          </div>
          <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t bg-white px-6 py-4">
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="rounded-sm border px-3 py-1.5 text-sm text-neutral-primary hover:bg-neutral-primary/5"
            >
              Annuleren
            </button>
            {mode !== 'view' && (
              <button
                onClick={handleSubmit}
                disabled={isLoading}
                className="rounded-sm bg-moss-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-moss-primary/90"
              >
                {isLoading ? 'Bezig…' : mode === 'edit' ? 'Polis bijwerken' : 'Polis toevoegen'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Building Selector Modal */}
      <BuildingSelector
        isOpen={buildingsOpen}
        onClose={() => setBuildingsOpen(false)}
        selectedBuildingIds={formData.buildingIds}
        onSave={(buildingIds) => setFormData(prev => ({ ...prev, buildingIds }))}
      />
      </>
    ) : null
  );
};

export default AddPolicyModal;
